import { encodeString, decodeString } from '../../../encoders/index.mjs';
import { ALPHANUMERIC_REGEX, ENCODED_ELEMENT_TAG, ENCODED_TAG_NAME_ATTR, ENCODED_TAG_VALUE_ATTR, ENCODED_ATTR_PREFIX } from '../constants.mjs';
import { isValidAttributeName, extractEncodedData } from './utilities.mjs';

// When this function terminates, currentElement will get processed by DOMPurify's sanitizer
// regarless of what we do in here. If that element is a dangerous element it will
// get stripped out which is why we need to append an encoded replacement to the DOM
function encodeDangerousElementHook(currentElement, hookEvent) {
  const {
    tagName,
    allowedTags
  } = hookEvent;
  if (allowedTags[tagName]) {
    return;
  }
  const isInvalidTagName = !ALPHANUMERIC_REGEX.test(currentElement.tagName);
  if (isInvalidTagName) {
    return;
  }
  const encodedNode = sanitizeDangerousElement(currentElement, tagName);
  if (encodedNode) {
    var _currentElement$paren;
    (_currentElement$paren = currentElement.parentNode) == null ? void 0 : _currentElement$paren.insertBefore(encodedNode, currentElement);
    currentElement.innerHTML = '';
  }
}
function sanitizeDangerousElement(element, tagName) {
  const {
    innerHTML: tagContents,
    ownerDocument: domPurifyDOM
  } = element;
  if (!domPurifyDOM) {
    return null;
  }
  const attributes = Array.from(element.attributes);
  const encodedElement = domPurifyDOM.createElement(ENCODED_ELEMENT_TAG);
  encodedElement.setAttribute(ENCODED_TAG_NAME_ATTR, tagName);
  encodedElement.setAttribute(ENCODED_TAG_VALUE_ATTR, encodeString(tagContents));
  attributes.filter(({
    name
  }) => isValidAttributeName(name, domPurifyDOM)).forEach(({
    name,
    value
  }) => {
    const encodedAttrName = `${ENCODED_ATTR_PREFIX}${name}`;
    encodedElement.setAttribute(encodedAttrName, encodeString(value));
  });
  return encodedElement;
}
function unsanitizeEncodedElements(element) {
  const {
    tagName,
    ownerDocument: domPurifyDOM
  } = element;
  if (!domPurifyDOM) {
    return null;
  }
  const attributes = Array.from(element.attributes);
  const isEncodedElement = attributes.some(({
    name: attrName
  }) => {
    return ENCODED_TAG_NAME_ATTR === attrName;
  });

  // Normalize tagName since it is uppercase when HTML and lowercase when SVG
  const isEncodedTagType = tagName.toLowerCase() === ENCODED_ELEMENT_TAG.toLowerCase();
  if (!isEncodedTagType || !isEncodedElement) {
    return;
  }
  const {
    tag: unencodedTag,
    attributes: unencodedAttributes
  } = extractEncodedData(element);
  const unencodedElement = document.createElement(unencodedTag.name);
  domPurifyDOM.adoptNode(unencodedElement);
  if (unencodedTag.value) {
    unencodedElement.innerHTML = decodeString(unencodedTag.value);
  }
  unencodedAttributes.filter(({
    name
  }) => isValidAttributeName(name, domPurifyDOM)).forEach(({
    name,
    value
  }) => {
    unencodedElement.setAttribute(name, decodeString(value));
  });
  element.parentElement.replaceChild(unencodedElement, element);
}

export { encodeDangerousElementHook, unsanitizeEncodedElements };
